import firebase from "firebase/app";

import { Button, Divider } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";

import actionNames from "astrid-distribution/src/actions/actionNames";
import { db } from "astrid-firebase";
import updateOrganization from "astrid-firestore/src/api/organizations/updateOrganization";
import { useCollectionData } from "astrid-firestore/src/hooks/index";
import { nullable, object, string } from "astrid-firestore/src/types/_types";

import Message from "../../../ui/components/Messages/Message";

import Form from "../../../forms/components/Form/Form";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";
import PipelineStepActionArgs from "../../../pipelines/components/PipelineForm/components/PipelineStepActionArgs";

const actionOptions = Object.entries(actionNames).map(([key, value]) => ({
	key: value,
	value,
	text: key,
}));

export default function PublisherExportPresetForm({ publisher, editData = null, onClose }) {
	const { t } = useTranslation();

	const isAdding = !editData;

	const [availableExportPresets] = useCollectionData(
		isAdding ? db.collection("exportPresets").where("deleted", "==", false) : null,
	);

	const selectableExportPresets = availableExportPresets
		?.filter((preset) => !publisher.exportPresets?.[preset.id])
		.map((preset) => ({ key: preset.id, value: preset.id, text: preset.name }));

	const initialOverrides = editData?.actions
		? Object.entries(editData.actions).map(([action, { args, conditionalArgs }]) => {
				const { condition, ...conditionalArgValues } = conditionalArgs || {};
				return {
					action,
					args: args || null,
					condition: condition || null,
					conditionalSection: { args: conditionalArgs ? conditionalArgValues : null },
				};
		  })
		: [];

	const { form, onSubmit, error, watch } = useSimpleForm({
		schema: object({
			exportPresetId: string(1),
			actionOverrides: object({
				action: string(1),
				args: nullable(object().passthrough()),
				condition: nullable(string()),
				conditionalSection: object({
					args: nullable(object().passthrough()),
				}).default({}),
			})
				.array()
				.default([]),
		}),
		defaultValues: {
			exportPresetId: editData?.id || null,
			actionOverrides: initialOverrides,
		},
		onSubmit: ({ exportPresetId, actionOverrides }) => {
			const actions = (actionOverrides || []).reduce((acc, { action, args, condition, conditionalSection }) => {
				if (action) {
					acc[action] = {
						args: args || null,
						conditionalArgs: condition ? { condition, ...conditionalSection?.args } : null,
					};
				}
				return acc;
			}, {});

			return updateOrganization(firebase, publisher.ref, {
				exportPresets: {
					[exportPresetId]: { actions },
				},
			});
		},
		onSuccess: onClose,
	});

	const { fields, append, remove } = Form.useFieldArray({ name: "actionOverrides", control: form.control });
	const { exportPresetId, actionOverrides } = watch();

	return (
		<Form.Modal
			header={isAdding ? t("addExportPreset", "Add export preset") : editData.name}
			form={form}
			error={error}
			onSubmit={onSubmit}
			onClose={onClose}
			scrolling
		>
			{isAdding &&
				(selectableExportPresets?.length > 0 ? (
					<Form.Select
						name="exportPresetId"
						label={t("exportPreset", "Export preset")}
						options={selectableExportPresets}
					/>
				) : (
					<Message info>{t("allExportPresetsAdded", "All export presets have already been added.")}</Message>
				))}

			{fields.map((field, index) => {
				const { action, condition } = actionOverrides?.[index] || {};
				return (
					<div
						key={field.id}
						style={{
							border: "1px solid var(--color-border)",
							borderRadius: 4,
							padding: "1em",
							marginBottom: "0.75em",
						}}
					>
						<Form.Select
							name={`actionOverrides.${index}.action`}
							label={t("action", "Action")}
							options={actionOptions}
						/>

						{action && <PipelineStepActionArgs name={`actionOverrides.${index}`} action={action} />}

						<Divider />

						<Form.Input name={`actionOverrides.${index}.condition`} label={t("condition", "Condition")} />

						{action && condition && (
							<PipelineStepActionArgs
								name={`actionOverrides.${index}.conditionalSection`}
								action={action}
							/>
						)}

						<Button type="button" size="mini" basic onClick={() => remove(index)}>
							{t("remove", "Remove")}
						</Button>
					</div>
				);
			})}

			<Button
				type="button"
				disabled={!exportPresetId}
				basic
				size="small"
				icon="plus"
				content={t("addActionOverride", "Add action override")}
				onClick={() =>
					append({ action: null, args: null, condition: null, conditionalSection: { args: null } })
				}
			/>
		</Form.Modal>
	);
}
